import React, { useCallback, useEffect } from "react";
import {
  ColumnDef,
  OnChangeFn,
  Row,
  Table,
  Updater,
} from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";
import { FaFilter, FaColumns } from "react-icons/fa";

import {
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ExpandedState,
  getExpandedRowModel,
} from "@tanstack/react-table";

import { Pagination } from "./Pagination";
import { TableHeader } from "./TableHeader";
import { TAN_TABLE_CONSTS, buildApiFilter } from "./TanTable.consts";
import { TanTableActions } from "./TanTableActions";
import { PaziaButton } from "../../atoms";
import { PaziaModal } from "../Modals/PaziaModal"; // adjust import if needed
import { useColumnVisibility } from "./useColumnVisibility";
import { useIsMobile } from "utils/hooks/useIsMobile";

declare module "@tanstack/table-core" {
  interface PaginationState {
    pageIndex: number;
    pageSize: number;
    total: number;
  }
}
// Force column id by TypeScript
export type TanTableColumn<T> = ColumnDef<T> & {
  id: string; // id is now required
  tdClassName?: string;
  // Hidden by default below md (useIsMobile); the viewer can still turn it on in the column chooser.
  hideOnMobile?: boolean;
  // Used by CSV/XLSX/PDF/print export instead of the raw accessor value, for columns whose
  // accessor holds a non-primitive (e.g. a tranche object) that can't be stringified sensibly.
  getExportValue?: (row: T) => string | number | boolean | null | undefined;
};

export interface BaseTanTableProps<T> {
  onRowClick?: (rowOrginal: any, row?: any) => void;
  onEdit?: (row: any) => void;
  onPreview?: (row: any) => void;
  onDelete?: (row: any) => void;
  onDuplicate?: (row: any) => void;
  onClearFilters?: () => void;
  data: any[];
  hideBorder?: boolean;
  columns: TanTableColumn<T>[];
  onSelectionChange?: (selectedRows: any[]) => void;
  className?: string;
}

// TODO https://codesandbox.io/p/sandbox/github/gargroh/react-table-plugins/tree/master/examples/export-data?file=%2Fsrc%2FApp.js%3A3%2C63-4%2C53

type TanTableFilter = { id: string; value: string; apiFilter: string };
interface TanTableProps<T> extends BaseTanTableProps<T> {
  loading: boolean;
  fetching: boolean;
  total: number;
  onPaginationChange?: (pagination: any) => void;
  onSortingChange?: (sorting: any) => void;
  onFiltersChange?: (filters: TanTableFilter[]) => void;
  pagination?: any;
  sorting?: any;
  initialFilters?: ColumnFiltersState;
}

export const TanTable = (props: TanTableProps<any>) => {
  const {
    loading,
    data,
    onPaginationChange,
    onClearFilters,
    onSortingChange,
    onFiltersChange,
    total,
    pagination,
    fetching,
    sorting,
    onRowClick,
    onEdit,
    onDelete,
    onPreview,
    onDuplicate,
    onSelectionChange,
    hideBorder = false,
    className = "",
    initialFilters,
  } = props;
  const [headerRenderKey, setHeaderRenderKey] = React.useState(1);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    initialFilters ?? []
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [selectedRows, setSelectedRows] = React.useState<Set<number>>(
    new Set()
  );
  const { columns } = props;
  const isMobile = useIsMobile();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(columns, isMobile);
  // Only show column visibility icon if all columns have unique ids
  const allColumnsHaveIds = columns.every((col) => !!col.id);
  const [showColumnsModal, setShowColumnsModal] = React.useState(false);

  const handleCheckboxClick = useCallback(
    (row: Row<any>) => {
      if (!hasObjectWithId(row)) {
        console.error("Row does not have id property");
        return;
      }

      const newSelectedRows = new Set(selectedRows);
      if (newSelectedRows.has(row.original.id)) {
        newSelectedRows.delete(row.original.id);
      } else {
        newSelectedRows.add(row.original.id);
      }
      setSelectedRows(newSelectedRows);
      if (onSelectionChange) {
        onSelectionChange(Array.from(newSelectedRows));
      }
    },
    [selectedRows, onSelectionChange]
  );

  // Handle filter changes, supporting both direct values and updater functions
  const handleFiltersChange: OnChangeFn<ColumnFiltersState> = (
    updaterOrValue
  ) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? // @ts-ignore
          (updaterOrValue as Updater<ColumnFiltersState>)(columnFilters)
        : updaterOrValue;

    setColumnFilters(newFilters);

    if (onFiltersChange) {
      // @ts-ignore TODO
      const formattedFilters = newFilters.map((filter) => {
        // Find the associated column definition using the filter's id
        const column = columns.find(
          // @ts-ignore TODO
          (col) => col.accessorKey === filter.id || col.id === filter.id
        );
        return buildApiFilter(filter, (column?.meta as any)?.filterVariant);
      });
      onFiltersChange(formattedFilters);
    }
  };

  // A filter typed on a column that phones hide (or restored from storage) must not filter the list invisibly:
  // on phones, every column with an active filter stays visible.
  const filteredColumnIds = columnFilters
    .filter(({ value }) => value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0))
    .map(({ id }) => id);
  const visibleColumns = React.useMemo(
    () =>
      isMobile ? { ...columnVisibility, ...Object.fromEntries(filteredColumnIds.map((id) => [id, true])) } : columnVisibility,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnVisibility, isMobile, filteredColumnIds.join("|")],
  );

  const table = useReactTable({
    data,
    columns,
    onColumnFiltersChange: handleFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange,
    onSortingChange,
    state: {
      columnFilters,
      pagination: { ...pagination, total },
      sorting,
      expanded,
      columnVisibility: visibleColumns,
    },
    onColumnVisibilityChange: setColumnVisibility,
    pageCount: Math.ceil(total / pagination.pageSize),
  });

  const anyColumnWithFilter = columns.some(
    (column) => column.enableColumnFilter
  );
  return (
    <div className={className}>
      <div
        className={`overflow-x-auto ${hideBorder ? "" : "rounded-xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-800"} ${
          fetching ? "opacity-50" : ""
        }`}
      >
        <div className="flex justify-end gap-2">
          {/* Columns visibility button */}
          {allColumnsHaveIds && (
            <button
              className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={() => setShowColumnsModal(true)}
              title="Wybierz kolumny"
              type="button"
            >
              <FaColumns />
            </button>
          )}
          {onClearFilters && (
            <PaziaButton
              type="tertiary"
              innerClassName="flex items-center gap-1"
              onClick={() => {
                table.resetColumnFilters();
                setHeaderRenderKey(Math.random());
                // Lets the page also reset filters kept outside the table (category trees/selects); it runs after
                // the reset's onFiltersChange, which still re-applies them from the page's current state
                onClearFilters?.();
              }}
            >
              <FaFilter className="text-slate-600 dark:text-slate-400" />
              Wyczyść
            </PaziaButton>
          )}
        </div>
        {/* Columns Modal */}
        {allColumnsHaveIds && (
          <PaziaModal
            isOpen={showColumnsModal}
            onCloseRequest={() => {
              setShowColumnsModal(false);
              setHeaderRenderKey(Math.random());
            }}
            id="columns-modal"
            size="md"
          >
            <div className="p-4">
              <h3 className="font-bold mb-2">Wybierz widoczne kolumny</h3>
              <div className="flex flex-col gap-2">
                {columns.map((col) => (
                  <label key={col.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={columnVisibility[col.id] !== false}
                      onChange={() =>
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [col.id]: !prev[col.id],
                        }))
                      }
                    />
                    {typeof col.header === "function" ? col.id : col.header}
                  </label>
                ))}
              </div>
              <div className="flex justify-end mt-4">
                <button
                  className="px-4 py-2 bg-slate-700 text-white rounded"
                  onClick={() => {
                    setShowColumnsModal(false);
                    setHeaderRenderKey(Math.random());
                  }}
                  type="button"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </PaziaModal>
        )}
        <table className="table-auto w-full dark:text-slate-300">
          <TableHeader
            table={table}
            // TableHeader is memoized on the (stable) table instance; remount it when the visible columns change
            key={`${headerRenderKey}-${isMobile}-${isMobile ? filteredColumnIds.join("|") : ""}`}
            ignoreColumnSizes={isMobile}
            setSelectedRows={setSelectedRows}
            numberOfSelectedRows={selectedRows.size}
            onSelectionChange={onSelectionChange}
            renderFilters={anyColumnWithFilter}
          />
          <TableBody
            table={table}
            columns={columns}
            onRowClick={onRowClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onPreview={onPreview}
            selectedRows={selectedRows}
            onSelectionChange={onSelectionChange}
            handleCheckboxClick={handleCheckboxClick}
          />
        </table>
      </div>
      {pagination && (
        <Pagination
          table={table}
          sizes={TAN_TABLE_CONSTS.pageSizeOptions}
          isLoading={loading ?? false}
          total={total}
        />
      )}
    </div>
  );
};

interface TableBodyProps {
  table: Table<any>;
  columns: any;
  onRowClick?: (rowOrginal: any, row?: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onDuplicate?: (row: any) => void;
  onPreview?: (row: any) => void;
  selectedRows: Set<any>;
  onSelectionChange?: (selectedRows: any[]) => void;
  handleCheckboxClick: (row: any) => void;
  // Extra <tr>s rendered directly below a row (e.g. an expanded detail list). colSpan covers the full row width.
  renderSubRows?: (rowOriginal: any, colSpan: number) => React.ReactNode;
  // Limits row clicks (and the pointer cursor) to some rows; all rows are clickable when omitted.
  isRowClickable?: (rowOriginal: any) => boolean;
}
export const TableBody = ({
  table,
  columns,
  onRowClick,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  selectedRows,
  onSelectionChange,
  handleCheckboxClick,
  renderSubRows,
  isRowClickable,
}: TableBodyProps) => {
  return (
    <tbody className="text-sm border-b border-slate-200 dark:border-slate-700">
      {table.getRowModel().rows.length === 0 && (
        <tr>
          <td colSpan={columns.length + 1} className="text-center p-4">
            Brak wyników
          </td>
        </tr>
      )}
      {table.getRowModel().rows.map((row) => {
        const rowClassName =
          "bg-white text-black divide-y divide-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:divide-slate-700 hover:bg-slate-200 hover:dark:bg-slate-700";

        const hasActionsCell = [onEdit, onDelete].filter(Boolean).length > 0;
        const clickable = !!onRowClick && (!isRowClickable || isRowClickable(row.original));
        const colSpan =
          row.getVisibleCells().length + (onSelectionChange ? 1 : 0) + (hasActionsCell ? 1 : 0);

        return (
          <React.Fragment key={row.id ?? row.original.id}>
            <tr
              className={`w-14 ${rowClassName} ${
                clickable ? "cursor-pointer" : ""
              }`}
              onClick={() => clickable && onRowClick(row.original, row)}
            >
              {onSelectionChange && (
                <td
                  className={`px-2 first:pl-[14px] last:pr-5 py-1 text-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCheckboxClick(row);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.original.id)}
                    onChange={() => handleCheckboxClick(row)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
              )}
              {row.getVisibleCells().map((cell) => {
                const columeDef = cell.column.columnDef as TanTableColumn<any>;
                return (
                  <td
                    key={uuidv4()}
                    className={
                      `px-2 first:pl-5 last:pr-5 py-1 text-ellipsis overflow-hidden` +
                      (columeDef.tdClassName ? ` ${columeDef.tdClassName}` : "")
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
              <TanTableActions
                onEdit={onEdit}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onPreview={onPreview}
                rowOriginal={row.original}
              />
            </tr>
            {renderSubRows?.(row.original, colSpan)}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};

export function hasObjectWithId(obj: any): obj is { original: { id: number } } {
  return obj && obj.original && obj.original.id;
}
